import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Card,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  createMetalRewardRequest,
  fetchMetalRewardsRequest,
  getMetalRewardRequest,
} from '../../../redux/MetalRewards/metalRewardsSlice';
import MetalRewardsList from './MetalRewardsList';
import { canShowPermission } from '../Permissions/permissionObject';

const types = ['Percentage', 'Fixed'];
const DIAMOND_POINT_VALUE = 'diamond_point';
const DIAMOND_POINT_TYPES = ['FIXED', 'PERCENTAGE'];

const MetalRewards = () => {
  const dispatch = useDispatch();
  const { currentReward, creating } = useSelector(
    (state) => state.metalRewards,
  );

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem('userPermissions');
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse userPermissions', e);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, 'metalRewards', 'create');
  const canEdit = canShowPermission(permissions, 'metalRewards', 'edit');
  const canDelete = canShowPermission(permissions, 'metalRewards', 'delete');

  // Initial form state
  const initFormData = {
    buyMetal: '',
    rewardMetal: '',
    percentage: '',
    offerType: '',
    type: 'Percentage',
    startDate: null,
    endDate: null,
    isDiamondPoint: false,
    diamondPointType: '',
    diamondPointValue: '',
  };

  const [formData, setFormData] = useState(initFormData);
  const isDiamondPointSelected = formData.rewardMetal === DIAMOND_POINT_VALUE;
  // Prefill form only if editing and form is empty
  useEffect(() => {
    if (
      currentReward &&
      !formData.buyMetal &&
      !formData.rewardMetal &&
      !formData.percentage
    ) {
      setFormData({
        buyMetal: currentReward.buyMetal?._id || '',
        rewardMetal: currentReward.isDiamondPoint
          ? DIAMOND_POINT_VALUE
          : currentReward.rewardMetal?._id || '',
        percentage: currentReward.percentage || '',
        offerType: currentReward.offerOn || '',
        type: currentReward.type
          ? currentReward.type.charAt(0).toUpperCase() +
            currentReward.type.slice(1)
          : '',
        startDate: currentReward.startDate
          ? new Date(currentReward.startDate)
          : null,
        endDate: currentReward.endDate ? new Date(currentReward.endDate) : null,
        diamondPointType: currentReward.diamondPointType
          ? currentReward.diamondPointType.charAt(0).toUpperCase() +
            currentReward.diamondPointType.slice(1)
          : '',
        diamondPointValue:
          currentReward.diamondPointValue !== undefined &&
          currentReward.diamondPointValue !== null
            ? currentReward.diamondPointValue
            : '',
      });
    }
  }, [currentReward]);

  // Fetch metals and metal rewards table on mount
  useEffect(() => {
    dispatch(getMetalRewardRequest({ id: 'id' })); // fetch single reward
    // dispatch(fetchMetalRewardsRequest()); // fetch table list
  }, [dispatch]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const handleRewardMetalChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      rewardMetal: value,
      diamondPointType:
        value === DIAMOND_POINT_VALUE ? prev.diamondPointType : '',
      diamondPointValue:
        value === DIAMOND_POINT_VALUE ? prev.diamondPointValue : '',
    }));
  };

  const handleSubmit = () => {
    // Validation
    if (!formData.buyMetal || !formData.rewardMetal) {
      toast.error('Please select Buy Metal and Reward Metal');
      return;
    }
    if (!isDiamondPointSelected) {
      if (
        !formData.percentage ||
        formData.percentage <= 0 ||
        formData.percentage > 100
      ) {
        toast.error('Please enter a valid percentage (0.001 - 100)');
        return;
      }
    }
    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select both Start and End dates');
      return;
    }
    if (formData.startDate > formData.endDate) {
      toast.error('Start Date cannot be after End Date');
      return;
    }
    if (isDiamondPointSelected) {
      if (!formData.diamondPointType) {
        toast.error('Please select Diamond Point Type');
        return;
      }
      if (formData.diamondPointType === 'FIXED') {
        if (!Number.isInteger(Number(formData.diamondPointValue))) {
          toast.error('Diamond Point value must be a whole number');
          return;
        }
      }
      if (
        !formData.diamondPointValue ||
        Number(formData.diamondPointValue) <= 0
      ) {
        toast.error('Please enter a valid Diamond Point value');
        return;
      }
      if (
        formData.diamondPointType === 'PERCENTAGE' &&
        Number(formData.diamondPointValue) > 100
      ) {
        toast.error('Diamond Point percentage cannot exceed 100');
        return;
      }
    }

    const payload = {
      buyMetal: formData.buyMetal,
      rewardMetal: isDiamondPointSelected ? '' : formData.rewardMetal,
      type: 'percentage',
      offerOn: formData.offerType || 'invoice',
      percentage: Number(formData.percentage),
      startDate: new Date(formData.startDate).getTime(),
      endDate: new Date(formData.endDate).getTime(),
      enabled: true,
      isDiamondPoint: isDiamondPointSelected,
      ...(isDiamondPointSelected && {
        diamondPointType: formData.diamondPointType.toUpperCase(),
        diamondPointValue: Number(formData.diamondPointValue),
      }),
    };

    dispatch(createMetalRewardRequest(payload));
    // toast.success('Metal Reward Saved Successfully!');

    // Reset form after submit
    setFormData(initFormData);

    // Refresh list
    dispatch(fetchMetalRewardsRequest());
  };

  return (
    <>
      <Card
        sx={{
          width: '100%',
          mr: 2,
          mt: 0,
          mb: 2,
          pt: 3,
          background: '#fff',
          borderRadius: '3px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          p: 2,
        }}
      >
        <Typography fontWeight={700} fontSize={24} fontFamily={'Aeonik'}>
          Metal Rewards
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            {/* Buy Metal */}
            <Grid item xs={12} sm={4}>
              <TextField
                sx={{ width: '350px' }}
                select
                label="Buy Metal"
                fullWidth
                required
                value={formData.buyMetal}
                onChange={(e) => handleChange('buyMetal', e.target.value)}
                disabled={!canEdit}
              >
                {currentReward?.metals?.length > 0 ? (
                  currentReward.metals.map((metal) => (
                    <MenuItem key={metal._id} value={metal._id}>
                      {metal.metalName}-{metal.karat}k-{metal.purity}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>Loading...</em>
                  </MenuItem>
                )}
              </TextField>
            </Grid>

            {/* Reward Metal */}
            <Grid item xs={12} sm={4}>
              <TextField
                sx={{ width: '350px' }}
                select
                label="Reward Metal"
                fullWidth
                required
                value={formData.rewardMetal}
                onChange={(e) => handleRewardMetalChange(e.target.value)}
                disabled={!canEdit}
              >
                <MenuItem value={DIAMOND_POINT_VALUE}>💎Diamond Point</MenuItem>
                {currentReward?.metals?.length > 0 ? (
                  currentReward.metals.map((metal) => (
                    <MenuItem key={metal._id} value={metal._id}>
                      {metal.metalName}-{metal.karat}k-{metal.purity}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>Loading...</em>
                  </MenuItem>
                )}
              </TextField>
            </Grid>

            {/* Diamond Point Type - only shown once Diamond Point is selected as Reward Metal */}
            {isDiamondPointSelected && (
              <Grid item xs={12} sm={4}>
                <TextField
                  sx={{ width: '350px' }}
                  select
                  label="Diamond Point Type"
                  fullWidth
                  required
                  value={formData.diamondPointType}
                  onChange={(e) =>
                    handleChange('diamondPointType', e.target.value)
                  }
                  disabled={!canEdit}
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  {DIAMOND_POINT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {/* Diamond Point Value - only shown once a type is chosen */}
            {isDiamondPointSelected && formData.diamondPointType && (
              <Grid item xs={12} sm={4}>
                <TextField
                  sx={{ width: '350px' }}
                  label={
                    formData.diamondPointType === 'PERCENTAGE'
                      ? 'Diamond Point Percentage'
                      : 'Diamond Point Value'
                  }
                  placeholder={
                    formData.diamondPointType === 'PERCENTAGE'
                      ? 'e.g. 2 (%)'
                      : 'e.g. 1 (points)'
                  }
                  fullWidth
                  required
                  type="number"
                  inputProps={
                    formData.diamondPointType === 'PERCENTAGE'
                      ? { min: 0.001, max: 100, step: 0.001 }
                      : { min: 1, step: 1 }
                  }
                  value={formData.diamondPointValue}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (formData.diamondPointType === 'FIXED') {
                      // Allow only integers
                      if (value === '' || /^\d+$/.test(value)) {
                        handleChange('diamondPointValue', value);
                      }
                    } else {
                      handleChange('diamondPointValue', value);
                    }
                  }}
                  disabled={!canEdit}
                />
              </Grid>
            )}

            {/* Select Type */}
            {!isDiamondPointSelected && (
              <Grid item xs={12} sm={4}>
                <TextField
                  sx={{ width: '350px' }}
                  select
                  label="Select Type"
                  fullWidth
                  // value={formData.type}
                  value="Percentage"
                  disabled
                  // onChange={(e) => handleChange("type", e.target.value)}
                >
                  {/* {types.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))} */}
                  <MenuItem value="Percentage">Percentage</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* Percentage */}
            {!isDiamondPointSelected && (
              <Grid item xs={12} sm={4}>
                <TextField
                  sx={{ width: '350px' }}
                  label="Enter Percentage"
                  placeholder="This percentage is transferred in customer account."
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0.001, max: 100, step: 0.001 }}
                  value={formData.percentage}
                  onChange={(e) => handleChange('percentage', e.target.value)}
                  disabled={!canEdit}
                />
              </Grid>
            )}

            {/* Start Date */}
            <Grid item xs={12} sm={4}>
              <DateTimePicker
                sx={{ width: '350px' }}
                label="Start Offer Date & Time"
                value={formData.startDate}
                onChange={(newVal) => {
                  handleChange('startDate', newVal);

                  // reset endDate if it becomes invalid
                  if (
                    formData.endDate &&
                    newVal &&
                    newVal >= formData.endDate
                  ) {
                    handleChange('endDate', null);
                  }
                }}
                format="dd/MM/yyyy HH:mm"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    disabled: !canEdit,
                  },
                }}
                disabled={!canEdit}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={4}>
              <DateTimePicker
                sx={{ width: '350px' }}
                label="End Offer Date & Time"
                value={formData.endDate}
                onChange={(newVal) => handleChange('endDate', newVal)}
                minDateTime={formData.startDate || undefined}
                format="dd/MM/yyyy HH:mm"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    disabled: !canEdit,
                  },
                }}
                disabled={!canEdit}
              />
            </Grid>

            {/* Offer Type */}
            <Grid item xs={12} sm={4}>
              <Box mt={2}>
                <FormControl fullWidth size="small" sx={{ width: '300px' }}>
                  <InputLabel>Offer Type</InputLabel>
                  <Select
                    name="offerType"
                    value={formData.offerType}
                    onChange={(e) => handleChange('offerType', e.target.value)}
                    disabled={!canEdit}
                  >
                    <MenuItem value="">
                      <em>Select Offer Type</em>
                    </MenuItem>
                    {/* <MenuItem value="EVERY_INSTALLMENT_DISCOUNT">
                      EVERY INSTALLMENT DISCOUNT
                    </MenuItem>
                    <MenuItem value="PREPAID_FIRST_INSTALLMENT">
                      PREPAID FIRST INSTALLMENT
                    </MenuItem> */}
                    <MenuItem value="First-Transaction">
                      Metal back on First Transaction
                    </MenuItem>
                    <MenuItem value="All-Transactions">
                      Metal back on All Transactions
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* Save Button */}
        {(canCreate || canEdit) && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="secondary"
              disabled={creating}
              sx={{
                textTransform: 'none',
                background:
                  'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
              }}
              onClick={handleSubmit}
            >
              {creating ? 'Saving...' : 'SAVE'}
            </Button>
          </Box>
        )}
      </Card>

      {/* Metal Rewards Table */}
      <Card sx={{ mt: 3 }}>
        <MetalRewardsList />
      </Card>
    </>
  );
};

export default MetalRewards;
